import { Box, Grid, Typography } from '@mui/material';

import { FCheckbox, FBoxContainer, FHSelect } from '../../../base';
import { useParentSupervisionEditable } from '../../../../hooks';
import { FORTA_BLACK, FORTA_BLUE } from '../../../../utils/colors';
import { SupervisionNoteStatus } from '../../../../constants';
import { PARENT_PROGRESS_OPTIONS, boxContainerHeaderStyle, contentStyle } from './constants';

import type { IParentSupervision, SelectValueType } from '../../../../utils/types';
import type { ChangeEvent } from 'react';

interface ParentProgressProps {
  parentSNLoading: boolean;
  parentSupervision: IParentSupervision | null;
  onUpdate: (data: IParentSupervision) => void;
}

function ParentProgress(props: ParentProgressProps) {
  const { parentSNLoading, parentSupervision, onUpdate } = props;
  const isEditable = useParentSupervisionEditable() && !parentSNLoading;
  const isSupervisionCompleted = parentSupervision?.status === SupervisionNoteStatus.Completed;

  return (
    <Grid container justifyContent="flex-end" mt={{ md: -27.5, xs: 2 }}>
      <Grid item xs={12} md={5.9}>
        <FBoxContainer
          sx={{
            pb: 2,
            borderRadius: '20px',
            flexGrow: 0,
            boxShadow: 'none',
            height: 220,
          }}
          headerStyle={boxContainerHeaderStyle}
          contentStyle={contentStyle}
        >
          <Typography
            fontSize={21}
            mt={1}
            mb={isSupervisionCompleted ? 5 : 3}
            color={FORTA_BLUE.primary}
            textAlign="left"
          >
            Parent progress
          </Typography>
          <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flexGrow: 1,
              }}
            >
              <Typography
                fontSize={16}
                fontWeight={700}
                mb={isSupervisionCompleted ? 0 : 1}
                textAlign="left"
                color={FORTA_BLACK}
              >
                Progress
              </Typography>
              {isSupervisionCompleted ? (
                <Typography
                  fontSize={16}
                  paddingTop={1}
                  paddingBottom={1}
                  textAlign="left"
                  color={FORTA_BLACK}
                >
                  {PARENT_PROGRESS_OPTIONS.find(
                    (option) => option.value === parentSupervision?.parent_progress,
                  )?.label || ''}
                </Typography>
              ) : (
                <FHSelect
                  data-testid="parentsProgressDropDown"
                  sx={{
                    flexGrow: 1,
                    '.MuiInputBase-input': {
                      border: `1px solid ${FORTA_BLUE.light3}`,
                    },
                    width: '100%',
                    boxShadow: 'none',
                  }}
                  inputHeight={40}
                  disabled={!isEditable}
                  options={PARENT_PROGRESS_OPTIONS}
                  optionDataTestIdPrefix="parentProgress"
                  value={parentSupervision?.parent_progress || ''}
                  onUpdate={(value: SelectValueType) => {
                    if (!parentSupervision) return;

                    onUpdate({
                      ...parentSupervision,
                      parent_progress: String(value),
                    });
                  }}
                />
              )}
            </Grid>
            <Grid
              item
              md={8}
              xs={12}
              sx={{ justifySelf: 'flex-end', alignSelf: 'flex-end', paddingBottom: 1 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <FCheckbox
                  data-testid="parentAdheresWithGoalsAndTargetsCheckbox"
                  label="Parent adheres with goals and targets"
                  iconStyles={{ boxShadow: 'none' }}
                  disabled={!isEditable}
                  checked={Boolean(parentSupervision?.goals_and_targets_adhered)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (!parentSupervision) return;

                    onUpdate({
                      ...parentSupervision,
                      goals_and_targets_adhered: e.target.checked,
                    });
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </FBoxContainer>
      </Grid>
    </Grid>
  );
}

export default ParentProgress;
